import React, { useEffect, useRef, useState } from "react";
import { api, getAuthToken, API } from "@/lib/api";
import {
  X, Upload, Phone, Mail, Calendar, MessageSquare, FileText,
  Building2, MapPin, Download, Trash2, Paperclip, Sparkles, Send, Clock, Target
} from "lucide-react";

const STAGES = [
  { v: "new", l: "New" },
  { v: "follow_up", l: "Follow Up" },
  { v: "qualified", l: "Qualified" },
  { v: "closed", l: "Closed" },
];

const CATS = [
  { v: "general", l: "General" },
  { v: "residential", l: "Residential" },
  { v: "commercial", l: "Commercial" },
  { v: "dental", l: "Dental Practice" },
  { v: "medical", l: "Medical Clinic" },
];

const SOURCES = [
  { v: "website", l: "Website" },
  { v: "call", l: "Call-in" },
  { v: "referral", l: "Referral" },
  { v: "event", l: "Event" },
  { v: "email", l: "Email" },
  { v: "social", l: "Social Media" },
  { v: "walk_in", l: "Walk-in" },
  { v: "other", l: "Other" },
];

const PRIORITIES = [
  { v: "low", l: "Low", cls: "bg-slate-100 text-slate-700" },
  { v: "medium", l: "Medium", cls: "bg-blue-100 text-blue-800" },
  { v: "high", l: "High", cls: "bg-amber-100 text-amber-800" },
  { v: "urgent", l: "Urgent", cls: "bg-rose-100 text-rose-800" },
];

const KIND_ICONS = {
  note: MessageSquare,
  call: Phone,
  email: Mail,
  meeting: Calendar,
  status: Building2,
};

const fmtDate = (iso) => new Date(iso).toLocaleString();
const fmtBytes = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

export const LeadDetailDrawer = ({ leadId, onClose, onUpdate }) => {
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [files, setFiles] = useState([]);
  const [tab, setTab] = useState("timeline");
  const [note, setNote] = useState({ body: "", kind: "note" });
  const [savingField, setSavingField] = useState(null);
  const [edit, setEdit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [l, n, f] = await Promise.all([
        api.get(`/crm/leads/${leadId}`),
        api.get(`/crm/leads/${leadId}/notes`),
        api.get(`/crm/leads/${leadId}/files`),
      ]);
      setLead(l.data); setNotes(n.data); setFiles(f.data);
      setEdit(l.data);
    } catch { onClose(); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.body.trim()) return;
    await api.post(`/crm/leads/${leadId}/notes`, note);
    setNote({ body: "", kind: "note" });
    const n = await api.get(`/crm/leads/${leadId}/notes`);
    setNotes(n.data);
  };

  const delNote = async (nid) => {
    if (!window.confirm("Delete this timeline entry?")) return;
    await api.delete(`/crm/leads/${leadId}/notes/${nid}`);
    const n = await api.get(`/crm/leads/${leadId}/notes`);
    setNotes(n.data);
  };

  const upload = async (fileObj) => {
    if (!fileObj) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", fileObj);
    try {
      await api.post(`/crm/leads/${leadId}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const [f, n] = await Promise.all([
        api.get(`/crm/leads/${leadId}/files`),
        api.get(`/crm/leads/${leadId}/notes`),
      ]);
      setFiles(f.data); setNotes(n.data);
    } catch (e) {
      alert("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const delFile = async (fid) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/crm/leads/${leadId}/files/${fid}`);
    const f = await api.get(`/crm/leads/${leadId}/files`);
    setFiles(f.data);
  };

  const saveField = async (field, value) => {
    setSavingField(field);
    try {
      await api.patch(`/crm/leads/${leadId}`, { [field]: value });
      const n = await api.get(`/crm/leads/${leadId}/notes`);
      setNotes(n.data);
      setLead((prev) => ({ ...prev, [field]: value }));
      onUpdate?.();
    } finally {
      setSavingField(null);
    }
  };

  const saveBulk = async () => {
    const changed = {};
    for (const k of ["name", "email", "phone", "source", "referral_source", "category",
                     "priority", "budget_min", "budget_max", "timeline", "location_preference",
                     "company", "assigned_agent_id"]) {
      if (edit[k] !== lead[k]) changed[k] = edit[k];
    }
    if (Object.keys(changed).length === 0) return;
    // Coerce numbers
    if ("budget_min" in changed) changed.budget_min = Number(changed.budget_min) || null;
    if ("budget_max" in changed) changed.budget_max = Number(changed.budget_max) || null;
    await api.patch(`/crm/leads/${leadId}`, changed);
    load();
    onUpdate?.();
  };

  if (!lead) return null;
  const priority = PRIORITIES.find((p) => p.v === lead.priority) || PRIORITIES[1];

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-navy/70" />
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-start justify-between z-10">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className={`text-xs px-2 py-0.5 uppercase tracking-wider font-semibold ${priority.cls}`} data-testid="lead-drawer-priority">{priority.l}</span>
              <span className="text-xs px-2 py-0.5 bg-gold/20 text-navy uppercase tracking-wider font-semibold" data-testid="lead-drawer-category">{lead.category || lead.interest || "general"}</span>
              <span className="text-xs text-slate-400">{lead.source}</span>
            </div>
            <h2 className="font-display text-3xl text-navy" data-testid="lead-drawer-name">{lead.name}</h2>
            <div className="text-sm text-slate-600 mt-1 flex flex-wrap gap-4">
              <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4 text-gold" />{lead.email}</span>
              {lead.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4 text-gold" />{lead.phone}</span>}
              {lead.company && <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4 text-gold" />{lead.company}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-navy" data-testid="lead-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="border-b border-slate-200">
          <div className="flex gap-0 px-6">
            {["timeline", "details", "files", "ai"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-3 text-sm font-semibold uppercase tracking-widest border-b-2 transition ${
                  tab === t ? "border-gold text-navy" : "border-transparent text-slate-500 hover:text-navy"
                }`}
                data-testid={`lead-tab-${t}`}
              >
                {t === "timeline" ? "Timeline" :
                 t === "details" ? "Details" :
                 t === "files" ? `Files (${files.length})` :
                 (<span className="inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-gold" />AI</span>)}
              </button>
            ))}
          </div>
        </div>

        {tab === "timeline" && (
          <div className="p-6 space-y-6" data-testid="lead-timeline-tab">
            <form onSubmit={addNote} className="bg-slate-50 border border-slate-200 p-4" data-testid="lead-note-form">
              <div className="flex gap-2 mb-3">
                {["note", "call", "email", "meeting"].map((k) => {
                  const Icon = KIND_ICONS[k];
                  return (
                    <button type="button" key={k} onClick={() => setNote({ ...note, kind: k })}
                      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border ${note.kind === k ? "border-gold bg-white text-navy font-semibold" : "border-slate-200 text-slate-500"}`}
                      data-testid={`lead-note-kind-${k}`}>
                      <Icon className="w-3.5 h-3.5" /> {k}
                    </button>
                  );
                })}
              </div>
              <textarea required rows="3" placeholder={`Log a ${note.kind}… e.g. "Called ${lead.name}, left voicemail, following up Thu."`}
                value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gold bg-white"
                data-testid="lead-note-body" />
              <div className="mt-2 flex justify-end">
                <button className="btn-navy text-sm py-2 px-4" data-testid="lead-note-submit">Add Entry</button>
              </div>
            </form>

            <div className="space-y-4">
              {notes.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No entries yet. Log calls, emails, and meetings above.</div>}
              {notes.map((n) => {
                const Icon = KIND_ICONS[n.kind] || MessageSquare;
                return (
                  <div key={n.id} className="flex gap-4 group" data-testid={`lead-note-${n.id}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                        n.kind === "status" ? "bg-gold/20 text-gold" :
                        n.kind === "call" ? "bg-emerald-100 text-emerald-700" :
                        n.kind === "email" ? "bg-blue-100 text-blue-700" :
                        n.kind === "meeting" ? "bg-amber-100 text-amber-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="w-px flex-1 bg-slate-200 mt-2" />
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-slate-500">
                          <span className="font-semibold text-navy">{n.author_name}</span> · <span className="uppercase tracking-wider">{n.kind}</span> · {fmtDate(n.created_at)}
                        </div>
                        <button onClick={() => delNote(n.id)} className="opacity-0 group-hover:opacity-100 text-xs text-red-500" data-testid={`lead-note-delete-${n.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="mt-1 text-sm text-navy whitespace-pre-wrap">{n.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "details" && (
          <div className="p-6 space-y-6" data-testid="lead-details-tab">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Stage" testId="lead-detail-stage">
                <select value={lead.stage} onChange={(e) => saveField("stage", e.target.value)} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-detail-stage-select">
                  {STAGES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </Field>
              <Field label="Priority">
                <select value={edit.priority} onChange={(e) => { setEdit({ ...edit, priority: e.target.value }); saveField("priority", e.target.value); }} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-detail-priority">
                  {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select value={edit.category || "general"} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-detail-category">
                  {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </Field>
              <Field label="Source">
                <select value={edit.source} onChange={(e) => setEdit({ ...edit, source: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-detail-source">
                  {SOURCES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </Field>
              <Field label="Name">
                <input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-detail-name" />
              </Field>
              <Field label="Email">
                <input type="email" value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-detail-email" />
              </Field>
              <Field label="Phone">
                <input value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-detail-phone" />
              </Field>
              <Field label="Company / Practice Name">
                <input value={edit.company || ""} onChange={(e) => setEdit({ ...edit, company: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-detail-company" />
              </Field>
              <Field label="Referral Source / Campaign">
                <input placeholder="e.g. Dr. Rao, Instagram Ad" value={edit.referral_source || ""} onChange={(e) => setEdit({ ...edit, referral_source: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-detail-referral" />
              </Field>
              <Field label="Location Preference">
                <input placeholder="e.g. Austin, TX" value={edit.location_preference || ""} onChange={(e) => setEdit({ ...edit, location_preference: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-detail-location" />
              </Field>
              <Field label="Budget Min ($)">
                <input type="number" value={edit.budget_min ?? ""} onChange={(e) => setEdit({ ...edit, budget_min: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-detail-budget-min" />
              </Field>
              <Field label="Budget Max ($)">
                <input type="number" value={edit.budget_max ?? ""} onChange={(e) => setEdit({ ...edit, budget_max: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lead-detail-budget-max" />
              </Field>
              <Field label="Timeline">
                <select value={edit.timeline || ""} onChange={(e) => setEdit({ ...edit, timeline: e.target.value })} className="w-full border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="lead-detail-timeline">
                  <option value="">— Select —</option>
                  <option value="< 3 months">&lt; 3 months</option>
                  <option value="3-6 months">3–6 months</option>
                  <option value="6-12 months">6–12 months</option>
                  <option value="12+ months">12+ months</option>
                </select>
              </Field>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <button onClick={() => setEdit(lead)} className="text-sm text-slate-500 px-4 py-2">Discard</button>
              <button onClick={saveBulk} className="btn-navy text-sm py-2 px-5" data-testid="lead-detail-save">Save Details</button>
            </div>
          </div>
        )}

        {tab === "files" && (
          <div className="p-6" data-testid="lead-files-tab">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-slate-300 p-8 text-center bg-slate-50 hover:bg-slate-100 transition"
              data-testid="lead-upload-dropzone"
            >
              <Upload className="w-8 h-8 text-gold mx-auto mb-3" strokeWidth={1.5} />
              <div className="text-sm text-navy font-semibold">Upload a document</div>
              <div className="text-xs text-slate-500 mt-1">Contracts, NDAs, LOIs, financials — up to 20MB. Drag & drop or click below.</div>
              <input ref={fileRef} type="file" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])} data-testid="lead-upload-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="btn-navy mt-4 text-sm py-2 px-5" data-testid="lead-upload-btn">
                {uploading ? "Uploading…" : "Choose File"}
              </button>
            </div>

            <div className="mt-6 space-y-2">
              {files.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No documents uploaded yet.</div>}
              {files.map((f) => {
                const token = getAuthToken();
                const dlUrl = `${API}/crm/leads/${leadId}/files/${f.id}/download?auth=${encodeURIComponent(token || "")}`;
                return (
                  <div key={f.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200" data-testid={`lead-file-${f.id}`}>
                    <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-navy truncate">{f.original_filename}</div>
                      <div className="text-xs text-slate-500">
                        {fmtBytes(f.size)} · Uploaded by {f.uploaded_by_name} · {fmtDate(f.created_at)}
                      </div>
                    </div>
                    <a href={dlUrl} target="_blank" rel="noreferrer" className="text-xs text-navy hover:text-gold inline-flex items-center gap-1" data-testid={`lead-file-download-${f.id}`}>
                      <Download className="w-4 h-4" /> Open
                    </a>
                    <button onClick={() => delFile(f.id)} className="text-xs text-red-500" data-testid={`lead-file-delete-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {tab === "ai" && (
          <AITab leadId={leadId} lead={lead} onLeadRefresh={load} />
        )}
      </div>
    </div>
  );
};

const AITab = ({ leadId, lead, onLeadRefresh }) => {
  const [qualify, setQualify] = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [busy, setBusy] = useState(null);
  const [outreachCfg, setOutreachCfg] = useState({ channel: "email", tone: "luxury", goal: "intro" });
  const [followupCh, setFollowupCh] = useState("email");

  useEffect(() => {
    api.get(`/ai/followups/${leadId}`).then((r) => setFollowups(r.data)).catch(() => {});
  }, [leadId]);

  const runQualify = async () => {
    setBusy("qualify"); setQualify(null);
    try {
      const r = await api.post("/ai/qualify", { lead_id: leadId });
      setQualify(r.data);
      onLeadRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Qualify failed");
    } finally { setBusy(null); }
  };

  const runOutreach = async () => {
    setBusy("outreach"); setOutreach(null);
    try {
      const r = await api.post("/ai/outreach", { lead_id: leadId, ...outreachCfg });
      setOutreach(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Outreach failed");
    } finally { setBusy(null); }
  };

  const runFollowup = async () => {
    setBusy("followup");
    try {
      await api.post("/ai/followup-plan", { lead_id: leadId, channel: followupCh });
      const r = await api.get(`/ai/followups/${leadId}`);
      setFollowups(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Follow-up plan failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="lead-ai-tab">
      {/* Qualification */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline text-gold flex items-center gap-1.5"><Target className="w-3 h-3" /> AI Qualification</div>
            <h3 className="font-display text-xl text-navy mt-1">Score this lead</h3>
            <p className="text-xs text-slate-500 mt-1">Urgency, fit, budget signals, conversion odds.</p>
          </div>
          <button onClick={runQualify} disabled={busy === "qualify"} className="btn-navy text-sm py-2 px-4 disabled:opacity-50" data-testid="ai-qualify-btn">
            <Sparkles className="w-4 h-4" /> {busy === "qualify" ? "Scoring…" : "Qualify"}
          </button>
        </div>
        {lead?.ai_score != null && !qualify && (
          <div className="text-xs text-slate-500 mb-2">Last score: <span className="font-semibold text-navy">{lead.ai_score}</span> ({lead.ai_tier || "—"})</div>
        )}
        {qualify?.score != null && (
          <div className="space-y-3" data-testid="ai-qualify-result">
            <div className="flex items-center gap-3">
              <div className="font-display text-5xl text-navy">{qualify.score}</div>
              <div>
                <div className={`text-xs font-bold uppercase tracking-widest px-2 py-0.5 ${
                  qualify.tier === "hot" || qualify.tier === "urgent" ? "bg-rose-100 text-rose-800" :
                  qualify.tier === "warm" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                }`}>{qualify.tier}</div>
                <div className="text-xs text-slate-500 mt-1">Suggested: priority {qualify.suggested_priority}, stage {qualify.suggested_stage}</div>
              </div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed italic">{qualify.rationale}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {qualify.strengths?.length > 0 && (
                <div className="bg-emerald-50 p-3 border-l-4 border-emerald-400">
                  <div className="text-xs font-bold uppercase text-emerald-900 mb-1">Strengths</div>
                  <ul className="text-xs text-emerald-900 space-y-1">{qualify.strengths.map((s, i) => <li key={i}>• {s}</li>)}</ul>
                </div>
              )}
              {qualify.risks?.length > 0 && (
                <div className="bg-rose-50 p-3 border-l-4 border-rose-400">
                  <div className="text-xs font-bold uppercase text-rose-900 mb-1">Risks</div>
                  <ul className="text-xs text-rose-900 space-y-1">{qualify.risks.map((s, i) => <li key={i}>• {s}</li>)}</ul>
                </div>
              )}
            </div>
            {qualify.recommended_actions?.length > 0 && (
              <div className="bg-slate-50 p-3">
                <div className="text-xs font-bold uppercase text-navy mb-1">Next actions</div>
                <ol className="text-xs text-slate-700 space-y-1 list-decimal list-inside">
                  {qualify.recommended_actions.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Outreach Writer */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline text-gold flex items-center gap-1.5"><Send className="w-3 h-3" /> AI Outreach</div>
            <h3 className="font-display text-xl text-navy mt-1">Draft a personal message</h3>
          </div>
          <button onClick={runOutreach} disabled={busy === "outreach"} className="btn-navy text-sm py-2 px-4 disabled:opacity-50" data-testid="ai-outreach-btn">
            <Sparkles className="w-4 h-4" /> {busy === "outreach" ? "Writing…" : "Generate"}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <select value={outreachCfg.channel} onChange={(e) => setOutreachCfg({ ...outreachCfg, channel: e.target.value })} className="border border-slate-200 px-2 py-1.5 text-xs bg-white" data-testid="ai-outreach-channel">
            <option value="email">Email</option><option value="sms">SMS</option><option value="whatsapp">WhatsApp</option>
          </select>
          <select value={outreachCfg.tone} onChange={(e) => setOutreachCfg({ ...outreachCfg, tone: e.target.value })} className="border border-slate-200 px-2 py-1.5 text-xs bg-white" data-testid="ai-outreach-tone">
            <option value="luxury">Luxury</option><option value="warm">Warm</option><option value="direct">Direct</option><option value="formal">Formal</option>
          </select>
          <select value={outreachCfg.goal} onChange={(e) => setOutreachCfg({ ...outreachCfg, goal: e.target.value })} className="border border-slate-200 px-2 py-1.5 text-xs bg-white" data-testid="ai-outreach-goal">
            <option value="intro">Intro</option><option value="follow_up">Follow-up</option><option value="nurture">Nurture</option><option value="book_meeting">Book meeting</option>
          </select>
        </div>
        {outreach && (
          <div className="bg-slate-50 p-4 space-y-2" data-testid="ai-outreach-result">
            {outreach.subject && <div className="text-sm font-semibold text-navy">Subject: {outreach.subject}</div>}
            <pre className="text-sm text-navy whitespace-pre-wrap font-sans leading-relaxed">{outreach.body}</pre>
            {outreach.cta && <div className="text-xs text-gold font-semibold mt-2">CTA: {outreach.cta}</div>}
            <button onClick={() => navigator.clipboard?.writeText(outreach.body)} className="text-xs text-navy hover:text-gold" data-testid="ai-outreach-copy">Copy to clipboard</button>
          </div>
        )}
      </div>

      {/* Follow-up Plan */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline text-gold flex items-center gap-1.5"><Clock className="w-3 h-3" /> AI Follow-up Plan</div>
            <h3 className="font-display text-xl text-navy mt-1">Day 1 · Day 3 · Day 7</h3>
          </div>
          <div className="flex items-center gap-2">
            <select value={followupCh} onChange={(e) => setFollowupCh(e.target.value)} className="border border-slate-200 px-2 py-1.5 text-xs bg-white" data-testid="ai-followup-channel">
              <option value="email">Email</option><option value="sms">SMS</option><option value="whatsapp">WhatsApp</option>
            </select>
            <button onClick={runFollowup} disabled={busy === "followup"} className="btn-navy text-sm py-2 px-4 disabled:opacity-50" data-testid="ai-followup-btn">
              <Sparkles className="w-4 h-4" /> {busy === "followup" ? "Planning…" : "Generate plan"}
            </button>
          </div>
        </div>
        {followups.length === 0 && <div className="text-xs text-slate-500 text-center py-4">No follow-up sequence yet.</div>}
        <div className="space-y-3">
          {followups.map((f) => (
            <div key={f.id} className="border-l-4 border-gold pl-4 py-1" data-testid={`ai-followup-${f.day}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500 uppercase tracking-widest">Day {f.day} · {f.channel}</div>
                <div className="text-xs text-slate-400">scheduled {new Date(f.scheduled_for).toLocaleDateString()}</div>
              </div>
              {f.subject && <div className="text-sm font-semibold text-navy mt-1">{f.subject}</div>}
              <div className="text-xs text-slate-600 mt-1">{f.purpose}</div>
              <pre className="text-sm text-navy whitespace-pre-wrap font-sans mt-2 leading-relaxed">{f.body}</pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <label className="block text-xs text-slate-500 uppercase tracking-widest mb-1.5">{label}</label>
    {children}
  </div>
);

export default LeadDetailDrawer;
